/**
 * Timesheets: what was rostered against what was actually worked.
 *
 * The variance column is the whole screen. A boss is not reading these rows to
 * admire them; they are looking for the ones that do not match, so anything
 * inside a few minutes is rendered quietly and anything outside it is coloured
 * and sorted to the top of the eye's path by contrast alone.
 *
 * Nothing is approved in bulk without being seen. "Approve all matching" is
 * offered because the rows it covers are, by definition, the ones with nothing
 * to look at.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CheckCheck, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { api, ApiError, qk, type Timesheet } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  addDays,
  fmtClock12,
  fmtDateRange,
  fmtDuration,
  fmtTimeRange,
  today,
  weekStart,
  type IsoDate,
} from '../../lib/time.ts';
import { Avatar, EmptyState, ErrorState, Field, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { Drawer, OutcomeModal } from '../../components/Overlay.tsx';

const NEAR_ENOUGH_MINUTES = 5;

function clockedMinutes(t: Timesheet): number {
  if (t.clockedMinutes != null) return t.clockedMinutes;
  if (!t.clockOff) return 0;
  const ms = new Date(t.clockOff).getTime() - new Date(t.clockOn).getTime();
  return Math.max(0, Math.round(ms / 60000) - t.breakMinutes);
}

function variance(t: Timesheet): number | null {
  if (t.varianceMinutes != null) return t.varianceMinutes;
  if (t.rosteredMinutes == null) return null;
  return clockedMinutes(t) - t.rosteredMinutes;
}

function VarianceCell({ t }: { t: Timesheet }) {
  const v = variance(t);
  if (v === null) {
    return (
      <span className="chip chip-caution" data-tip="No rostered shift|This clock on was not against anything on the roster.">
        Unrostered
      </span>
    );
  }
  if (Math.abs(v) <= NEAR_ENOUGH_MINUTES) {
    return <span className="tabular text-[12.5px] font-semibold text-faint">On time</span>;
  }
  const over = v > 0;
  return (
    <span
      className="tabular text-[13px] font-extrabold"
      style={{ color: over ? 'var(--sp-warn-strong)' : 'var(--sp-info)' }}
      data-tip={
        over
          ? `Worked longer than rostered|${fmtDuration(v)} more than the shift on the roster. Approving pays the worked time.`
          : `Worked less than rostered|${fmtDuration(-v)} short of the shift on the roster.`
      }
    >
      {over ? '+' : '-'}
      {fmtDuration(Math.abs(v))}
    </span>
  );
}

function AdjustDrawer({
  sheet,
  slug,
  onClose,
}: {
  sheet: Timesheet | null;
  slug: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [breakMinutes, setBreakMinutes] = useState(sheet?.breakMinutes ?? 0);
  const [notes, setNotes] = useState(sheet?.notes ?? '');
  const [failure, setFailure] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<string | null>(sheet?.id ?? null);

  if (sheet && seeded !== sheet.id) {
    setSeeded(sheet.id);
    setBreakMinutes(sheet.breakMinutes);
    setNotes(sheet.notes ?? '');
  }

  const save = useMutation({
    mutationFn: (approve: boolean) =>
      api.updateTimesheet(slug, sheet?.id ?? '', { breakMinutes, notes, ...(approve ? { approve: true } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['timesheets', slug] });
      onClose();
    },
    onError: (err) =>
      setFailure(err instanceof ApiError ? err.message : 'Could not save that adjustment. Try again in a moment.'),
  });

  return (
    <>
      <Drawer
        open={sheet !== null}
        onClose={onClose}
        title={sheet?.userName ?? 'Timesheet'}
        subtitle={sheet ? `${fmtClock12(sheet.clockOn)} to ${sheet.clockOff ? fmtClock12(sheet.clockOff) : 'still on'}` : undefined}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn" onClick={() => save.mutate(false)} disabled={save.isPending}>
              Save only
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => save.mutate(true)}
              disabled={save.isPending}
            >
              {save.isPending ? <Spinner size={15} label="Saving" /> : <Check size={15} aria-hidden="true" />}
              Save and approve
            </button>
          </>
        }
      >
        {sheet ? (
          <>
            <div
              className="mb-5 grid grid-cols-2 gap-3 rounded-card border p-4"
              style={{ background: 'var(--sp-surface-2)', borderColor: 'var(--sp-border)' }}
            >
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Rostered</div>
                <div className="tabular text-[15px] font-bold text-ink">
                  {sheet.rosteredStart && sheet.rosteredEnd
                    ? fmtTimeRange(sheet.rosteredStart, sheet.rosteredEnd)
                    : 'Not on the roster'}
                </div>
                <div className="tabular text-[12px] text-muted">
                  {sheet.rosteredMinutes != null ? fmtDuration(sheet.rosteredMinutes) : ''}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-faint">Clocked</div>
                <div className="tabular text-[15px] font-bold text-ink">
                  {fmtClock12(sheet.clockOn)} to {sheet.clockOff ? fmtClock12(sheet.clockOff) : 'still on'}
                </div>
                <div className="tabular text-[12px] text-muted">{fmtDuration(clockedMinutes(sheet))}</div>
              </div>
            </div>

            <Field label="Unpaid break" id="ts-break" hint="Minutes taken off the clocked time before it is paid.">
              <input
                id="ts-break"
                className="field tabular"
                type="number"
                min={0}
                step={5}
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(Number(e.target.value) || 0)}
              />
            </Field>
            <Field label="Note" id="ts-notes" hint="Why the times were changed. Worth writing for anything unusual.">
              <textarea
                id="ts-notes"
                className="field"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why was this adjusted?"
              />
            </Field>
          </>
        ) : null}
      </Drawer>
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not save"
        message={failure ?? ''}
      />
    </>
  );
}

export default function TimesheetsPage() {
  const { tenant } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const [week, setWeek] = useState<IsoDate>(() => weekStart(today()));
  const [adjusting, setAdjusting] = useState<Timesheet | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const query = useQuery({
    queryKey: qk.timesheets(slug, week),
    queryFn: () => api.timesheets(slug, week),
    enabled: slug !== '',
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.updateTimesheet(slug, id, { approve: true }),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.timesheets(slug, week) }),
    onError: (err) =>
      setFailure(err instanceof ApiError ? err.message : 'Could not approve that timesheet. Try again in a moment.'),
  });

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    return [...items].sort((a, b) => a.date.localeCompare(b.date) || (a.userName ?? '').localeCompare(b.userName ?? ''));
  }, [query.data?.items]);

  const matching = rows.filter((r) => {
    const v = variance(r);
    return r.status !== 'approved' && r.clockOff !== null && v !== null && Math.abs(v) <= NEAR_ENOUGH_MINUTES;
  });

  if (query.isLoading) return <LoadingBlock label="Loading timesheets" />;
  if (query.isError) {
    return <ErrorState error={query.error} what="timesheets" onRetry={() => void query.refetch()} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <header className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setWeek(addDays(week, -7))}
            title="Previous week"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setWeek(addDays(week, 7))}
            title="Next week"
            aria-label="Next week"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Timesheets</h1>
          <p className="text-[13px] text-muted">{fmtDateRange(week, addDays(week, 6))}</p>
        </div>
        <button
          type="button"
          className="btn"
          disabled={matching.length === 0}
          onClick={() => matching.forEach((m) => approve.mutate(m.id))}
          data-tip={
            matching.length === 0
              ? 'Nothing to approve|Every timesheet that matches its roster is already approved.'
              : `Approve ${matching.length} matching timesheets|Only the ones within ${NEAR_ENOUGH_MINUTES} minutes of the rostered shift. Anything with a real variance stays for you to look at.`
          }
        >
          <CheckCheck size={15} aria-hidden="true" />
          Approve {matching.length} matching
        </button>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          title="No timesheets this week"
          body="Timesheets appear here as the team clocks on and off. Nothing has been recorded for this week yet."
        />
      ) : (
        <div className="card overflow-hidden">
          <div
            className="grid px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-faint"
            style={{
              gridTemplateColumns: '1.6fr 0.8fr 1.1fr 1.1fr 0.8fr 0.9fr 96px',
              background: 'var(--sp-surface-2)',
              borderBottom: '1px solid var(--sp-border)',
            }}
          >
            <span>Who</span>
            <span>Day</span>
            <span>Rostered</span>
            <span>Clocked</span>
            <span>Paid</span>
            <span>Variance</span>
            <span className="text-right">Actions</span>
          </div>
          {rows.map((t, i) => (
            <div
              key={t.id}
              className="row-link grid items-center px-4 py-2.5"
              style={{
                gridTemplateColumns: '1.6fr 0.8fr 1.1fr 1.1fr 0.8fr 0.9fr 96px',
                borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined,
              }}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Avatar name={t.userName ?? '?'} size={28} />
                <span className="truncate text-[13.5px] font-semibold text-ink">{t.userName ?? 'Unknown'}</span>
              </span>
              <span className="text-[13px] text-muted">{t.date.slice(8)}/{t.date.slice(5, 7)}</span>
              <span className="tabular text-[13px] text-muted">
                {t.rosteredStart && t.rosteredEnd ? fmtTimeRange(t.rosteredStart, t.rosteredEnd) : 'Not rostered'}
              </span>
              <span className="tabular text-[13px] text-ink">
                {fmtClock12(t.clockOn)} to {t.clockOff ? fmtClock12(t.clockOff) : 'still on'}
              </span>
              <span className="tabular text-[13px] font-semibold text-ink">{fmtDuration(clockedMinutes(t))}</span>
              <span>
                <VarianceCell t={t} />
              </span>
              <span className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  className="btn btn-icon btn-sm"
                  onClick={() => setAdjusting(t)}
                  title={`Adjust ${t.userName ?? 'this timesheet'}|Change the break or add a note before approving.`}
                  aria-label="Adjust this timesheet"
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={t.status === 'approved' ? 'btn btn-icon btn-sm btn-ghost' : 'btn btn-icon btn-sm btn-primary'}
                  disabled={t.status === 'approved' || !t.clockOff || approve.isPending}
                  onClick={() => approve.mutate(t.id)}
                  title={
                    t.status === 'approved'
                      ? 'Approved|This timesheet is ready for the next pay run.'
                      : !t.clockOff
                        ? 'Still clocked on|They have not finished this shift yet.'
                        : 'Approve|Locks the hours in for the next pay run.'
                  }
                  aria-label={t.status === 'approved' ? 'Approved' : 'Approve this timesheet'}
                >
                  <Check size={14} aria-hidden="true" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <AdjustDrawer sheet={adjusting} slug={slug} onClose={() => setAdjusting(null)} />
      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not approve"
        message={failure ?? ''}
      />
    </div>
  );
}
